// YNX VISITOR TRACKER — privacy-friendly analytics client
// ───────────────────────────────────────────────────────────────
// Design goals:
//   • FAIL-SILENT: every public function is wrapped in try/catch.
//     A tracking error MUST NEVER break the visitor's UX.
//   • RGPD: tracking only runs after explicit cookie consent.
//   • Lightweight: no external SDK, ~3 KB minified.
//   • Async + non-blocking: uses navigator.sendBeacon when possible.
// ───────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_BACKEND_URL;

const VISITOR_KEY  = 'ynx_visitor_id';
const CONSENT_KEY  = 'ynx_consent';
const QUEUE_KEY    = 'ynx_track_queue';

// ─── Storage helpers (always fail-silent) ───────────────────────
const safeGet = (k) => {
  try { return localStorage.getItem(k); }
  catch (e) { console.debug('YNX tracker: localStorage read failed', e); return null; }
};
const safeSet = (k, v) => {
  try { localStorage.setItem(k, v); return true; }
  catch (e) { console.debug('YNX tracker: localStorage write failed', e); return false; }
};

// ─── Visitor identity (anonymous, persisted) ─────────────────────
const newVisitorId = () => {
  // 32 hex chars; collision probability is negligible vs business volume
  const arr = new Uint8Array(16);
  try {
    (window.crypto || window.msCrypto).getRandomValues(arr);
  } catch (e) {
    // Fallback for very old browsers (highly unlikely path)
    console.debug('YNX tracker: crypto fallback', e);
    for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const getVisitorId = () => {
  let id = safeGet(VISITOR_KEY);
  if (!id || id.length < 8) {
    id = newVisitorId();
    safeSet(VISITOR_KEY, id);
  }
  return id;
};

// ─── Consent (RGPD) ─────────────────────────────────────────────
// Possible values: 'accepted' | 'declined' | null (= not yet asked)
export const getConsent = () => safeGet(CONSENT_KEY);
export const setConsent = (value) => safeSet(CONSENT_KEY, value === 'accepted' ? 'accepted' : 'declined');
export const hasConsent = () => getConsent() === 'accepted';

// ─── Send helpers ───────────────────────────────────────────────
const buildPayload = (event_type, meta) => {
  let language = '';
  let screen = '';
  let tz = '';
  try {
    // Strip locale modifiers (`@posix`, `@euro`, …) before keeping the first 10 chars.
    language = String(navigator.language || '').split('@')[0].slice(0, 10);
  } catch (e) { console.debug('YNX tracker: navigator.language unavailable', e); }
  try { screen = `${window.screen.width}x${window.screen.height}`; }
  catch (e) { console.debug('YNX tracker: window.screen unavailable', e); }
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
  catch (e) { console.debug('YNX tracker: Intl.DateTimeFormat unavailable', e); }
  return {
    visitor_id: getVisitorId(),
    event_type: String(event_type || 'unknown').toLowerCase().slice(0, 40),
    path:       String(window.location.pathname || '').slice(0, 300),
    referrer:   String(document.referrer || '').slice(0, 500),
    language,
    screen:     screen.slice(0, 20),
    tz:         tz.slice(0, 60),
    user_agent: String(navigator.userAgent || '').slice(0, 400),
    meta:       meta && typeof meta === 'object' ? meta : null,
    consent:    hasConsent(),
  };
};

const sendBeacon = (payload) => {
  try {
    const url = `${API}/api/track`;
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return true;
  } catch (e) { console.debug('YNX tracker: sendBeacon failed', e); }
  return false;
};

const sendFetch = (payload) => {
  try {
    fetch(`${API}/api/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch((e) => { console.debug('YNX tracker: fetch rejected', e); });
    return true;
  } catch (e) {
    console.debug('YNX tracker: fetch threw', e);
    return false;
  }
};

// ─── Public API ─────────────────────────────────────────────────
/**
 * Record a visitor event. Fail-silent: never throws, never returns
 * a Promise that can be awaited (so callers don't accidentally wait).
 *
 * @param {string} event_type  Free-form short name (page_view, cta_click, ...)
 * @param {object} [meta]      Optional small JSON object with extra context
 */
export const track = (event_type, meta) => {
  try {
    if (!event_type) return;
    // Always send (server handles consent flag), but tag the payload.
    const payload = buildPayload(event_type, meta);
    if (!sendBeacon(payload)) sendFetch(payload);
  } catch (e) {
    // Absolute last-resort guard: log but never crash.
    console.debug('YNX tracker: track failed', e);
  }
};

/**
 * One-shot helper: track a page view exactly once per path per session.
 * Useful for SPA route changes where the same URL could fire multiple
 * effects.
 */
const _seenPaths = new Set();
export const trackPageView = (extraMeta) => {
  try {
    const key = window.location.pathname + window.location.search;
    if (_seenPaths.has(key)) return;
    _seenPaths.add(key);
    track('page_view', extraMeta);
  } catch (e) { console.debug('YNX tracker: page_view guard failed', e); }
};
